import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/manageTeamSlice";
import { UserCog, Building2 } from "lucide-react";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import RoleDropdown from "./RoleDropdown";
import OfficeDropdown from "./OfficeDropdown";
import { fetchTeamMembers, viewUserProfile, blockUser, unBlockUser } from "../api";
import { usePrivilege } from "../../../../guard/usePrivilege";
import Cookie from "js-cookie";
import axios from "axios";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { useTableSavedViews, TableSavedViewsControl } from "../../../TableSavedViews";

// ✅ CONSTANTS AT TOP
const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Names",
  "Email",
  "Job Title",
  "Phone Number",
  "Status",
  "Role",
  "Offices",
];

const SORTABLE_COLUMNS = {
  "Names": "firstName",
  "Email": "email",
  "Job Title": "job",
  "Phone Number": "phoneNumber",
  "Status": "isActive",
  "Role": "_role",
  "Offices": "_offices",
};

const memberStatusBadgeClass = {
  ACTIVE: "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  INACTIVE: "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  DEFAULT: "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

// ✅ MAIN COMPONENT
const TeamTable = ({ data }) => {
  // ✅ STATE DECLARATIONS
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage, setMembersPerPage] = useState(10);
  const [blockedMembers, setBlockedMembers] = useState([]);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [ascending, setAscending] = useState(true);

  // ✅ useTableSavedViews Hook (for column customization)
  const tableViews = useTableSavedViews({
    pageKey: "team-members-list",
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2, // Selection, Action are always visible
    defaultStickyColumnKeys: ["Selection", "Action", "Names"],
    getExtraConfig: () => ({
      currentPage,
      pageSize: membersPerPage,
    }),
    onApplyConfig: (config, _viewMeta, options = {}) => {
      if (config.currentPage) setCurrentPage(config.currentPage);
      if (config.pageSize) setMembersPerPage(config.pageSize);
    },
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // ✅ REFS
  const dropdownRef = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const dispatch = useDispatch();

  // ✅ HOOKS AND HELPERS
  const officeCode = Cookie.get("officeCode");
  const token = Cookie.get("authToken") || null;

  const { hasPrivilege } = usePrivilege();
  const canAssignRoles = hasPrivilege("teamMembers:ASSIGN_ROLES");
  const canAssignOffices = hasPrivilege("teamMembers:ASSIGN_OFFICES");
  const canAddContactstoOrgProfile = hasPrivilege("organizationProfile:assignContacts");

  // ✅ API CALL - Load Team Members
  const refreshTeamMembers = () => {
    const oc = Cookie.get("officeCode");
    const tk = Cookie.get("authToken") || null;
    fetchTeamMembers(oc, tk)
      .then((res) => {
        const sorted = [...res.data].sort((a, b) => a.email.localeCompare(b.email));
        setTeamMembers(sorted);
        setBlockedMembers(sorted.filter((m) => m.isBlocked).map((m) => m.email));
      })
      .catch(() => {
        setTeamMembers([]);
        setBlockedMembers([]);
      });
  };

  // ✅ USEEFFECT HOOKS
  useEffect(() => {
    refreshTeamMembers();
  }, [officeCode, token]);

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ✅ EVENT HANDLERS

  const handleSort = (columnHeader) => {
    if (sortBy === columnHeader) {
      setAscending(!ascending);
    } else {
      setSortBy(columnHeader);
      setAscending(true);
    }
    setCurrentPage(1);
    markDirty();
  };

  const handleActionClick = (email, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === email ? null : email));
  };

  const handleSelectAll = (checked) =>
    setSelectedMembers(checked ? teamMembers.map((m) => m.email) : []);

  const handleSingleCheckbox = (email) =>
    setSelectedMembers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );

  const handleBlockMember = async (row) => {
    try {
      await blockUser(row.email);
      refreshTeamMembers();
    } catch {}
  };

  const handleUnBlockMember = async (row) => {
    try {
      await unBlockUser(row.email);
      refreshTeamMembers();
    } catch {}
  };

  const handleAddAsContactPerson = async (row) => {
    try {
      const email = encodeURIComponent(row.email);
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/contact-persons/${email}/toggle?showOnOrgProfile=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify.success({ title: "Success" });
      refreshTeamMembers();
    } catch (err) {
      notify.error({
        title: `Failed to add ${row.email} as contact person`,
        message: err?.response?.data?.content || "",
      });
    }
  };

  const handleRowDoubleClick = async (member) => {
    try {
      const response = await viewUserProfile(member.email);
      dispatch(
        addTab({
          id: `user-profile-${member.email}`,
          name: `Profile: ${member.firstName || member.email}`,
          contentType: "viewUserProfile",
          data: response.data,
          isDynamic: true,
          createdAt: Date.now(),
        })
      );
    } catch (err) {
      console.error("Failed to load user profile", err);
    }
  };

  // ✅ SORTING LOGIC
  let effectiveData = data && data.length > 0 ? data : teamMembers;

  if (sortBy && SORTABLE_COLUMNS[sortBy]) {
    const sortKey = SORTABLE_COLUMNS[sortBy];
    effectiveData = [...effectiveData].sort((a, b) => {
      let aVal, bVal;

      if (sortKey === "firstName") {
        aVal = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase();
        bVal = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase();
      } else if (sortKey === "isActive") {
        aVal = a.isActive ? 1 : 0;
        bVal = b.isActive ? 1 : 0;
        return ascending ? aVal - bVal : bVal - aVal;
      } else if (sortKey === "_role") {
        aVal = (a.roles?.[0]?.name ?? "").toLowerCase();
        bVal = (b.roles?.[0]?.name ?? "").toLowerCase();
      } else if (sortKey === "_offices") {
        aVal = (a.organizations?.[0]?.organizationName ?? "").toLowerCase();
        bVal = (b.organizations?.[0]?.organizationName ?? "").toLowerCase();
      } else {
        aVal = (a[sortKey] ?? "").toLowerCase();
        bVal = (b[sortKey] ?? "").toLowerCase();
      }

      return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }

  const totalPages = Math.max(1, Math.ceil(effectiveData.length / membersPerPage));
  const paginatedMembers = effectiveData.slice(
    (currentPage - 1) * membersPerPage,
    currentPage * membersPerPage
  );

  // ✅ RETURN JSX
  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader
          color="text-blue-600 dark:text-acs-dark-text"
          size="h-5 w-5"
          message="Loading team members..."
        />
      ) : (
        <>
          {data !== null && data.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-gray-500 text-lg font-semibold">
                No matching team members found
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Try adjusting your search criteria
              </p>
            </div>
          )}

          {!(data !== null && data.length === 0) && (
            <>
              {/* COLUMN VIEWS CONTROL */}
              <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
                <TableSavedViewsControl
                  controlLabel={controlLabel}
                  views={views}
                  activeView={activeView}
                  loading={viewsLoading}
                  error={null}
                  isDirty={isDirty}
                  allDataHeaders={TABLE_HEADERS.slice(2)}
                  dataHeaders={dataHeaders}
                  onSelectView={selectView}
                  onSaveNew={saveNewView}
                  onUpdateActive={updateActiveView}
                  onRename={renameView}
                  onDelete={deleteView}
                  onSetDefault={setDefaultView}
                  onUpdateColumnVisibility={updateColumnVisibility}
                  onManageColumns={onManageColumns}
                />
              </div>

              {/* TABLE CONTAINER */}
              <div className="flex-1 overflow-x-auto">
                <Table
                  {...columnManagerProps}
                  headers={TABLE_HEADERS}
                  data={paginatedMembers}
                  initialColumnWidths={savedColumnWidths}
                  onColumnResize={onColumnResize}
                  onSelectAll={handleSelectAll}
                  renderCheckboxActions={() => setShowBulkMenu((prev) => !prev)}
                  checkboxHeaderRef={checkboxHeaderRef}
                  rowClassName={(row) =>
                    blockedMembers.includes(row.email) ? "opacity-50" : ""
                  }
                  enableColumnResize
                  enableColumnReorder
                  enableColumnPin
                  nonHideableColumnIds={["Selection", "Action", "Names"]}
                  sortBy={sortBy}
                  ascending={ascending}
                  onSort={handleSort}
                  sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
                >
                  {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
                    const isOpen = openDropdownRow === row.email;

                    const actions = [
                      {
                        icon: "Eye",
                        label: "View",
                        color: "blue",
                        action: () => handleRowDoubleClick(row),
                      },
                      row.isBlocked
                        ? {
                            icon: "LockKeyholeOpen",
                            label: "Unblock",
                            color: "green",
                            action: () => handleUnBlockMember(row),
                          }
                        : {
                            icon: "LockKeyhole",
                            label: "Block",
                            color: "orange",
                            action: () => handleBlockMember(row),
                          },
                      ...(canAddContactstoOrgProfile
                        ? [
                            {
                              icon: "UserPlus",
                              label: "Add as contact person",
                              color: "blue",
                              action: () => handleAddAsContactPerson(row),
                            },
                          ]
                        : []),
                      {
                        icon: "Trash",
                        label: "Remove",
                        color: "red",
                        action: async () => {
                          if (
                            !window.confirm(
                              `Are you sure you want to remove ${row.email} from this office?`
                            )
                          )
                            return;
                          try {
                            await axios.post(
                              `${process.env.REACT_APP_API_BASE_URL}/api/organizations/remove-user?officeCode=${officeCode}&userEmail=${encodeURIComponent(
                                row.email
                              )}`,
                              {},
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            notify.success({
                              title: "Member removed successfully",
                            });
                            refreshTeamMembers();
                          } catch (err) {
                            notify.error({
                              title: "Failed to remove member",
                              message: err?.response?.data?.content || "",
                            });
                          }
                          setOpenDropdownRow(null);
                        },
                      },
                    ];

                    return (
                      <>
                        {/* Column 0 — Checkbox */}
                        <td
                          className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                          style={{
                            width: "80px",
                            minWidth: "80px",
                            maxWidth: "80px",
                            boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                            checked={selectedMembers.includes(row.email)}
                            onChange={() => handleSingleCheckbox(row.email)}
                          />
                        </td>

                        {/* Column 1 — Action (portal dropdown) */}
                        <td
                          onClick={(e) => e.stopPropagation()}
                          className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                          style={{
                            width: "50px",
                            minWidth: "50px",
                            maxWidth: "50px",
                            boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                          }}
                        >
                          <button
                            data-action-trigger
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                            onClick={(e) => handleActionClick(row.email, e)}
                          >
                            <MoreHorizontal size={18} className="mx-auto" />
                          </button>
                        </td>

                        {isOpen &&
                          ReactDOM.createPortal(
                            <div
                              ref={dropdownRef}
                              style={{
                                position: "absolute",
                                top: dropdownPos.top,
                                left: dropdownPos.left,
                                zIndex: 9999,
                              }}
                            >
                              <ActionMenu
                                rowId={row.email}
                                isOpen
                                onClose={() => setOpenDropdownRow(null)}
                                moduleName="Team"
                                actions={actions}
                              />
                            </div>,
                            document.body
                          )}

                        {/* Data columns — respects visibleColumns for pin/hide/reorder */}
                        {(() => {
                          const orderedDataHeaders = (visibleColumns ?? [])
                            .map((col) => col.id)
                            .filter((id) => id !== "Selection" && id !== "Action");
                          const dataHeadersToRender =
                            orderedDataHeaders.length > 0
                              ? orderedDataHeaders
                              : dataHeaders;

                          return dataHeadersToRender.map((header) => {
                            const { className: colClass, style: colStyle } =
                              getStickyCellProps(header);

                            let content;
                            switch (header) {
                              case "Names":
                                content = `${row.firstName} ${row.lastName}`;
                                break;
                              case "Email":
                                content = row.email;
                                break;
                              case "Job Title":
                                content = row.jobTitle || "-";
                                break;
                              case "Phone Number":
                                content = row.phoneNumber || "-";
                                break;
                              case "Status":
                                content = (
                                  <span
                                    className={`px-fluid-1.5 py-[2px] rounded-md font-medium tracking-wide ${
                                      memberStatusBadgeClass[
                                        row.isActive ? "ACTIVE" : "INACTIVE"
                                      ] || memberStatusBadgeClass.DEFAULT
                                    }`}
                                  >
                                    {(row.isActive ? "ACTIVE" : "INACTIVE").toUpperCase()}
                                  </span>
                                );
                                break;
                              case "Role":
                                content = (
                                  <div className="bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-md shadow-sm p-fluid-2 flex flex-col gap-fluid-1 transition-none min-w-[16rem]">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center relative w-full">
                                        <UserCog className="w-4 h-4 text-[#223688] dark:text-acs-accent" />
                                        <h4 className="text-fluid-sm ml-fluid-2 font-semibold text-[#223688] dark:text-acs-accent">
                                          Assigned Roles
                                        </h4>
                                        {canAssignRoles && (
                                          <div className="ml-auto">
                                            <RoleDropdown
                                              userEmail={row.email}
                                              userRoles={row.roles}
                                              onRoleChange={refreshTeamMembers}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="border-t border-gray-200 dark:border-acs-dark-border" />
                                    <div className="flex flex-col gap-fluid-1">
                                      {row.roles && row.roles.length > 0 ? (
                                        row.roles.map((role) => (
                                          <div
                                            key={role.id}
                                            className={`flex items-center justify-between px-fluid-2 py-[0.4rem] rounded-md border text-fluid-xs font-medium transition-none ${
                                              role.active
                                                ? "bg-[#f9fbff] dark:bg-[#1c2438] border-[#cdd5ff] dark:border-[#30456f] text-gray-800 dark:text-acs-dark-text"
                                                : "bg-gray-50 dark:bg-[#23262d] border-gray-200 dark:border-[#3a3d44] text-gray-400 dark:text-acs-dark-subtext"
                                            }`}
                                          >
                                            <span
                                              className="truncate font-semibold max-w-[9rem]"
                                              title={role.name || "Unnamed"}
                                            >
                                              {role.name || "Unnamed"}
                                            </span>
                                            {role.active ? (
                                              <span className="text-[#28A745] dark:text-[#37d266] font-semibold">
                                                Active
                                              </span>
                                            ) : (
                                              <span className="text-gray-400 dark:text-acs-dark-subtext italic">
                                                Inactive
                                              </span>
                                            )}
                                          </div>
                                        ))
                                      ) : (
                                        <span className="text-gray-400 dark:text-acs-dark-subtext italic text-fluid-xs">
                                          No roles assigned
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                                break;
                              case "Offices":
                                content = (
                                  <div className="bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-md shadow-sm p-fluid-2 flex flex-col gap-fluid-1 transition-none min-w-[16rem]">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center relative w-full">
                                        <Building2 className="w-4 h-4 text-[#1B7F48] dark:text-acs-accent" />
                                        <h4 className="text-fluid-sm ml-fluid-2 font-semibold text-[#1B7F48] dark:text-acs-accent">
                                          Assigned Offices
                                        </h4>
                                        {canAssignOffices && (
                                          <div className="ml-auto">
                                            <OfficeDropdown
                                              userEmail={row.email}
                                              userOrganizations={row.organizations}
                                              officeCode={officeCode}
                                              onOfficeChange={refreshTeamMembers}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="border-t border-gray-200 dark:border-acs-dark-border" />
                                    <div className="flex flex-col gap-fluid-1">
                                      {row.organizations && row.organizations.length > 0 ? (
                                        row.organizations.map((org) => (
                                          <div
                                            key={org.officeCode}
                                            className="flex items-center justify-between px-fluid-2 py-[0.4rem] rounded-md border text-fluid-xs font-medium bg-[#f9fffa] dark:bg-[#1f2a25] border-[#c9efd7] dark:border-[#295c45] text-gray-800 dark:text-acs-dark-text transition-none"
                                          >
                                            <span
                                              className="font-semibold whitespace-nowrap break-keep flex-grow truncate"
                                              title={org.organizationName || "Unnamed"}
                                            >
                                              {org.organizationName || "Unnamed"}
                                            </span>
                                          </div>
                                        ))
                                      ) : (
                                        <span className="text-gray-400 dark:text-acs-dark-subtext italic text-fluid-xs">
                                          No offices assigned
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                                break;
                              default:
                                content = "--";
                            }

                            return (
                              <td
                                key={header}
                                className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
                                style={{
                                  ...colStyle,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                onDoubleClick={() => handleRowDoubleClick(row)}
                              >
                                {content}
                              </td>
                            );
                          });
                        })()}
                      </>
                    );
                  }}
                </Table>
              </div>

              {/* PAGINATION */}
              <GlobalPagination
                label="Members per page"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={membersPerPage}
                onItemsPerPageChange={setMembersPerPage}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TeamTable;